import React, { useRef, useState, useEffect } from "react";
import { Upload, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatError } from "@/lib/api";

/*
 * Shared image upload control for Events / News (admin).
 * Android-safe: a real <button> programmatically triggers a visually-hidden
 * (NOT display:none) file input via ref — labels tied to display:none inputs
 * fail to open the native picker on Android Chrome, especially inside modals.
 */
export function ImageUpload({ value, onChange, testId = "image-upload" }) {
  const inputRef = useRef(null);
  const previewUrlRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => () => { if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current); }, []);

  const setPreviewUrl = (file) => {
    if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    const url = file ? URL.createObjectURL(file) : null;
    previewUrlRef.current = url;
    setPreview(url);
  };

  const openPicker = () => {
    setError("");
    if (inputRef.current) {
      inputRef.current.value = ""; // allow re-selecting the same file
      inputRef.current.click();
    }
  };

  const handleChange = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setError("Please choose an image file.");
      toast.error("Please choose an image file.");
      return;
    }
    const localPreview = file;
    setPreviewUrl(localPreview);
    setUploading(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.path);
      toast.success("Image uploaded");
    } catch (err) {
      setPreviewUrl(null);
      if (err.response && err.response.status === 401) {
        setError("Your session has expired. Please sign in again — your entries are kept here.");
        toast.error("Session expired — please sign in again. Your entries are kept.");
      } else {
        const msg = formatError(err.response?.data?.detail) || "Upload failed. Please try again.";
        setError(msg);
        toast.error(msg);
      }
    } finally {
      setUploading(false);
    }
  };

  const done = !!value && !uploading;

  return (
    <div>
      <button
        type="button"
        data-testid={testId}
        onClick={openPicker}
        disabled={uploading}
        className="cu-input w-full px-3 py-2.5 text-sm flex items-center gap-2 cursor-pointer text-left"
      >
        {preview ? (
          <img src={preview} alt="" className="w-9 h-9 rounded object-cover shrink-0" />
        ) : uploading ? (
          <Loader2 size={15} className="text-[color:var(--cu-gold)] animate-spin" />
        ) : (
          <Upload size={15} className="text-[color:var(--cu-gold)]" />
        )}
        <span className="text-[color:var(--cu-muted)]">
          {uploading ? "Uploading…" : done ? "Change image" : "Upload image"}
        </span>
        {done && <Check size={15} className="ml-auto text-emerald-400" />}
      </button>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        onChange={handleChange}
        data-testid={`${testId}-input`}
        style={{ position: "absolute", width: 1, height: 1, padding: 0, margin: -1, overflow: "hidden", clip: "rect(0 0 0 0)", border: 0, opacity: 0 }}
      />
      {error && <p data-testid={`${testId}-error`} className="text-xs text-red-400 mt-1.5">{error}</p>}
    </div>
  );
}
